import React, { useEffect, useState } from "react";
import Icon from "../Shared/Icon";
import { claimKavachReward } from "../../Actions/Kavach";
import { KAVACH_COLORS, pickOptionIcon } from "./KavachData";

const KavachClaimModal = ({
  open,
  retailerCode,
  levelNumber,
  options = [],
  onClose,
  onClaim,
  onMoveNext,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimedOption, setClaimedOption] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedId(options.length === 1 ? options[0].optionId : null);
      setConfirmStep(false);
      setClaiming(false);
      setClaimError(null);
      setClaimedOption(null);
    }
  }, [open, options]);

  if (!open) return null;

  const selected = options.find((o) => o.optionId === selectedId);
  const isSingleOption = options.length <= 1;
  const isCureCoins = selected?.optionType === "COIN_EQUIVALENT";

  const handleClaim = () => {
    if (!selected) return;
    setClaiming(true);
    setClaimError(null);
    claimKavachReward(retailerCode, levelNumber, selected.optionId)
      .then(() => {
        setClaimedOption(selected);
        onClaim?.(selectedId);
      })
      .catch(() =>
        setClaimError("Couldn't claim your reward. Please try again."),
      )
      .finally(() => setClaiming(false));
  };

  if (claimedOption) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div
          style={styles.modal}
          className="kavach-pop-in"
          onClick={(e) => e.stopPropagation()}>
          <button
            style={styles.closeBtn}
            className="kavach-pressable"
            onClick={onClose}>
            <Icon name="close" size={16} color={KAVACH_COLORS.slate} />
          </button>

          <div style={styles.successBadge} className="kavach-pop-in">
            <Icon name="check" size={30} color="#fff" strokeWidth={3} />
          </div>

          <div style={styles.successTitle}>Reward Claimed!</div>
          <div style={styles.subtitle}>
            You claimed your Level {levelNumber} reward
          </div>

          <div style={styles.claimedCard}>
            <div style={styles.claimedIconWrap}>
              <Icon
                name={pickOptionIcon(claimedOption)}
                size={26}
                color="#fff"
              />
            </div>
            <div style={styles.optionText}>
              <div style={styles.optionTitle}>{claimedOption.title}</div>
              {claimedOption.description && (
                <div style={styles.optionDescription}>
                  {claimedOption.description}
                </div>
              )}
            </div>
          </div>

          <button
            className="kavach-pressable"
            style={{ ...styles.claimBtn, width: "100%", marginTop: 18 }}
            onClick={onClose}>
            Awesome, thanks!
          </button>
        </div>
      </div>
    );
  }

  if (confirmStep) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div
          style={styles.modal}
          className="kavach-pop-in"
          onClick={(e) => e.stopPropagation()}>
          <button
            style={styles.closeBtn}
            className="kavach-pressable"
            onClick={onClose}>
            <Icon name="close" size={16} color={KAVACH_COLORS.slate} />
          </button>

          <div style={styles.badge}>
            <Icon name="gift" size={22} color="#3A2A05" />
          </div>

          <div style={styles.title}>Confirm Your Claim</div>

          <div style={styles.claimedCard}>
            <div style={styles.claimedIconWrap}>
              <Icon name={pickOptionIcon(selected)} size={26} color="#fff" />
            </div>
            <div style={styles.optionText}>
              <div style={styles.optionTitle}>{selected?.title}</div>
              {selected?.description && (
                <div style={styles.optionDescription}>
                  {selected.description}
                </div>
              )}
            </div>
          </div>

          <div style={styles.disclaimerText}>
            This claim will result in an adjustment to the level and points.
            Kindly click "Confirm" to continue.
          </div>

          {isCureCoins && (
            <div style={styles.disclaimerText}>
              The coins will be instantly credited to your wallet.
            </div>
          )}

          {claimError && <div style={styles.claimErrorText}>{claimError}</div>}

          <div style={styles.footer}>
            <button
              className="kavach-pressable"
              style={styles.moveNextBtn}
              onClick={() => setConfirmStep(false)}
              disabled={claiming}>
              Back
            </button>
            <button
              className="kavach-pressable"
              style={{
                ...styles.claimBtn,
                ...(claiming ? styles.claimBtnDisabled : {}),
              }}
              disabled={claiming}
              onClick={handleClaim}>
              {claiming ? "Confirming…" : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={styles.modal}
        className="kavach-pop-in"
        onClick={(e) => e.stopPropagation()}>
        <button
          style={styles.closeBtn}
          className="kavach-pressable"
          onClick={onClose}>
          <Icon name="close" size={16} color={KAVACH_COLORS.slate} />
        </button>

        <div style={styles.badge}>
          <Icon name="gift" size={22} color="#3A2A05" />
        </div>

        <div style={styles.title}>Level {levelNumber} Reward Unlocked!</div>
        <div style={styles.subtitle}>
          {isSingleOption
            ? "Here's your reward — claim it below."
            : "Choose one reward, then claim it."}
        </div>

        <div style={styles.optionList}>
          {options.map((o) => {
            const isSelected = o.optionId === selectedId;
            return (
              <button
                key={o.optionId}
                className="kavach-pressable"
                style={{
                  ...styles.optionCard,
                  ...(isSelected ? styles.optionCardSelected : {}),
                  ...(isSingleOption ? { cursor: "default" } : {}),
                }}
                onClick={() => !isSingleOption && setSelectedId(o.optionId)}>
                <div
                  style={{
                    ...styles.optionIconWrap,
                    ...(isSelected ? styles.optionIconWrapSelected : {}),
                  }}>
                  <Icon
                    name={pickOptionIcon(o)}
                    size={20}
                    color={isSelected ? "#fff" : KAVACH_COLORS.purple}
                  />
                </div>
                <div style={styles.optionText}>
                  <div style={styles.optionTitle}>{o.title}</div>
                  {o.description && (
                    <div style={styles.optionDescription}>{o.description}</div>
                  )}
                </div>
                {!isSingleOption && (
                  <div
                    style={{
                      ...styles.radioOuter,
                      ...(isSelected ? styles.radioOuterSelected : {}),
                    }}>
                    {isSelected && <div style={styles.radioInner} />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div style={styles.footer}>
          {/* <button
            className="kavach-pressable"
            style={styles.moveNextBtn}
            onClick={onMoveNext}
            disabled={claiming}>
            Move to Next Level
          </button> */}
          <button
            className="kavach-pressable"
            style={{
              ...styles.claimBtn,
              ...(selected ? {} : styles.claimBtnDisabled),
            }}
            disabled={!selected}
            onClick={() => setConfirmStep(true)}>
            {`Claim ${selected ? `"${selected.title}"` : "Reward"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10,20,35,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 20,
  },
  modal: {
    position: "relative",
    width: 420,
    maxWidth: "100%",
    maxHeight: "85vh",
    overflowY: "auto",
    background: "#FFFFFF",
    borderRadius: 20,
    padding: "28px 24px 24px",
    boxShadow: "0 24px 60px rgba(10,20,35,0.35)",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "1px solid #E2E7EE",
    background: "#F7F9FC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  badge: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: `linear-gradient(150deg, ${KAVACH_COLORS.gold}, ${KAVACH_COLORS.goldDark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    boxShadow: "0 8px 20px rgba(255,165,0,0.35)",
  },
  successBadge: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: `linear-gradient(150deg, ${KAVACH_COLORS.green}, #1F8A54)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    boxShadow: "0 8px 20px rgba(46,169,103,0.35)",
  },
  successTitle: { fontSize: 19, fontWeight: 700, color: KAVACH_COLORS.ink },
  claimedCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    background: "#F1EEFB",
    border: `1.5px solid ${KAVACH_COLORS.purple}`,
    borderRadius: 14,
    padding: "14px 16px",
    marginTop: 6,
  },
  claimedIconWrap: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: 12,
    background: KAVACH_COLORS.purple,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: { fontSize: 18, fontWeight: 700, color: KAVACH_COLORS.ink },
  subtitle: {
    fontSize: 13,
    color: KAVACH_COLORS.slate,
    marginTop: 4,
    marginBottom: 18,
  },
  optionList: { display: "flex", flexDirection: "column", gap: 10 },
  optionCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    textAlign: "left",
    border: "1.5px solid #E7EAF0",
    background: "#FAFBFD",
    borderRadius: 14,
    padding: "12px 14px",
    cursor: "pointer",
  },
  optionCardSelected: {
    border: `1.5px solid ${KAVACH_COLORS.purple}`,
    background: "#F1EEFB",
  },
  optionIconWrap: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: 11,
    background: "#EDE6FB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  optionIconWrapSelected: {
    background: KAVACH_COLORS.purple,
  },
  optionText: { flex: 1, minWidth: 0 },
  optionTitle: { fontSize: 13.5, fontWeight: 600, color: KAVACH_COLORS.ink },
  optionDescription: {
    fontSize: 11.5,
    color: KAVACH_COLORS.slate,
    marginTop: 2,
  },
  radioOuter: {
    width: 20,
    height: 20,
    minWidth: 20,
    borderRadius: "50%",
    border: "2px solid #C7CDD6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuterSelected: { border: `2px solid ${KAVACH_COLORS.purple}` },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: KAVACH_COLORS.purple,
  },
  disclaimerText: {
    fontSize: 12.5,
    color: KAVACH_COLORS.slate,
    background: "#F7F9FC",
    border: "1px solid #E7EAF0",
    borderRadius: 10,
    padding: "10px 12px",
    marginTop: 12,
    lineHeight: 1.5,
  },
  claimErrorText: {
    marginTop: 14,
    fontSize: 12.5,
    fontWeight: 600,
    color: "#C0392B",
    background: "#FDECEA",
    borderRadius: 10,
    padding: "10px 12px",
  },
  footer: { display: "flex", gap: 10, marginTop: 20 },
  moveNextBtn: {
    flex: 1,
    border: "none",
    background: `linear-gradient(150deg, ${KAVACH_COLORS.indigo}, ${KAVACH_COLORS.purpleDark})`,
    borderRadius: 12,
    padding: "11px 16px",
    fontSize: 13.5,
    fontWeight: 700,
    color: "#FFFFFF",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(75,68,149,0.35)",
  },
  claimBtn: {
    flex: 1.4,
    border: "none",
    borderRadius: 12,
    padding: "11px 16px",
    fontSize: 13.5,
    fontWeight: 700,
    color: KAVACH_COLORS.ink,
    background: `linear-gradient(150deg, ${KAVACH_COLORS.gold}, ${KAVACH_COLORS.goldDark})`,
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(255,165,0,0.35)",
  },
  claimBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};

export default KavachClaimModal;
